import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from 'formik-mui';
import { Form, Formik, Field } from 'formik';
import { AnalyticsConsumer } from 'contexts/AnalyticsProvider';
import { DialogConsumer } from 'contexts/DialogProvider';
import { PATH_TERMS, PATH_PRIVACY, TRACK_EMAIL_CAPTURE_VIEWED } from 'constants';
import { InView } from 'react-intersection-observer';
import * as Yup from 'yup';
import AppDialog from 'components/Dialog/AppDialog';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from 'next/link';
import MuiLink from '@mui/material/Link';
import makeClasses from 'hooks/useClasses';
import useAlbumAdapter from 'adapters/albumAdapter';
import Typography from '@mui/material/Typography';
import styles from 'styles/jss/components/appDialogStyle';

const EmailCaptureDialog = () => {
  const { submitEmailSignup } = useAlbumAdapter();
  const { emailSignup, showEmailSignup } = DialogConsumer();
  const { isAuthenticated } = useAuth0();
  const { track } = AnalyticsConsumer();
  const classes = makeClasses(styles);

  /**
   * Only show signup if the content section is in view
   * @param {*} inView
   */
  const showSignup = (inView) => {
    // TODO: Only show if UTM? _.get(router, 'query.utm_source')
    if (inView && !isAuthenticated) {
      showEmailSignup(true);
      track(TRACK_EMAIL_CAPTURE_VIEWED);
    }
  };

  const closeDialog = () => {
    showEmailSignup(false);
  };

  const submitForm = async ({ email }, { resetForm }) => {
    const response = await submitEmailSignup(email);
    resetForm();
    if (response) {
      closeDialog();
    }
  };

  return (
    <>
      <Formik
        initialValues={{ email: '' }}
        validateOnMount
        enableReinitialize
        validationSchema={Yup.object().shape({
          email: Yup.string().email()
        })}
        onSubmit={submitForm}
      >
        {({ handleSubmit, isSubmitting, isValid }) => (
          <AppDialog
            open={emailSignup}
            handleClose={closeDialog}
            maxWidth="sm"
            // title="Email"
            buttons={[
              <LoadingButton
                key="save"
                type="submit"
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={!isValid}
                loading={isSubmitting}
              >
                Sign Up
              </LoadingButton>,
              <LoadingButton
                key="skip"
                type="submit"
                onClick={closeDialog}
                variant="outlined"
                color="primary"
              >
                No thanks, i love the clutter
              </LoadingButton>
            ]}
          >
            <Box className={classes.couponFrame} sx={{ p: 4, mt: 3 }}>
              <Form style={{ width: '100%' }}>
                <Typography
                  variant="bookPreview"
                  component="h1"
                  color="primary"
                  sx={{
                    mt: 2,
                    textAlign: 'center',
                    fontSize: '2.3rem'
                  }}
                >
                  Turn piles of art into something magical
                </Typography>
                <Typography
                  // variant="bookPreview"
                  component="h1"
                  sx={{
                    mt: 2,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.3rem'
                  }}
                >
                  Sign up for 20% off your first purchase!
                </Typography>
                <Field
                  component={TextField}
                  autoFocus
                  margin="dense"
                  name="email"
                  label="Email"
                  type="text"
                  fullWidth
                  multiline
                  required
                  color="secondary"
                  variant="standard"
                />
              </Form>
              <Typography
                // variant="bookPreview"
                // component="h3"
                sx={{
                  mt: 2,
                  fontSize: '0.8rem'
                }}
              >
                Submitting confirms that you have read and understood our
                {' '}
                <Link href={PATH_TERMS} passHref>
                  <MuiLink color="primary" underline="hover" component="a" target="_blank">
                    Terms of Use
                  </MuiLink>
                </Link>
                {' and '}
                <Link href={PATH_PRIVACY} passHref>
                  <MuiLink color="primary" underline="hover" component="a" target="_blank">
                    Privacy Policy
                  </MuiLink>
                </Link>
                {' '}
                (we will never share your details).
                Discount cannot be applied to prior or existing orders.
                Your 20% code will be delivered to your email address.
              </Typography>
            </Box>
          </AppDialog>
        )}
      </Formik>
      <InView
        onChange={showSignup}
        triggerOnce
        delay={500}
      >
        {({ ref }) => (
          <div ref={ref} />
        )}
      </InView>
    </>
  );
};

export default EmailCaptureDialog;
